import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Bell,
  Check,
  CheckCheck,
  CreditCard,
  Loader2,
  Megaphone,
} from 'lucide-react';
import {
  useMarkAllReadMutation,
  useMarkReadMutation,
  useNotificationsInfinite,
  type NotificationItem,
} from '@/features/notification/api/useNotificationQueries';

function getRelativeTime(dateString?: string) {
  if (!dateString) return '';
  const now = Date.now();
  const past = new Date(dateString).getTime();
  const mins = Math.floor((now - past) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
}

function getNotifStyle(type?: string, key?: string) {
  if (key?.includes('APPROVE') || type?.includes('success'))
    return { icon: <Check className="h-5 w-5" />, bg: 'bg-emerald-50', text: 'text-emerald-600' };
  if (key?.includes('REJECT') || type?.includes('failed') || type?.includes('alert'))
    return { icon: <AlertCircle className="h-5 w-5" />, bg: 'bg-red-50', text: 'text-red-600' };
  if (type?.includes('campaign'))
    return { icon: <Megaphone className="h-5 w-5" />, bg: 'bg-blue-50', text: 'text-blue-600' };
  if (type?.includes('wallet') || type?.includes('payment'))
    return { icon: <CreditCard className="h-5 w-5" />, bg: 'bg-indigo-50', text: 'text-indigo-600' };
  return { icon: <Bell className="h-5 w-5" />, bg: 'bg-gray-50', text: 'text-gray-600' };
}

function getScrollOffset(event: Event) {
  const target = event.target;
  if (
    target === document ||
    target === document.documentElement ||
    target === document.body ||
    target === window
  ) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  if (target instanceof HTMLElement) {
    return target.scrollTop;
  }
  return 0;
}

function isSentinelNearViewport(sentinel: HTMLElement) {
  const rect = sentinel.getBoundingClientRect();
  return rect.top <= window.innerHeight + 160;
}

function useNotificationInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  resetKey,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  resetKey: string;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const fetchingRef = useRef(false);

  useEffect(() => {
    hasScrolledRef.current = false;
    fetchingRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    const loadIfNeeded = () => {
      if (!hasScrolledRef.current) return;
      if (!hasNextPage || isFetchingNextPage || fetchingRef.current) return;
      const sentinel = sentinelRef.current;
      if (!sentinel || !isSentinelNearViewport(sentinel)) return;

      fetchingRef.current = true;
      void fetchNextPage().finally(() => {
        fetchingRef.current = false;
      });
    };

    const onScroll = (event: Event) => {
      if (getScrollOffset(event) > 8) {
        hasScrolledRef.current = true;
      }
      loadIfNeeded();
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', onScroll, {
        capture: true,
      } as EventListenerOptions);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (isFetchingNextPage || !hasNextPage) return;
    const sentinel = sentinelRef.current;
    if (!hasScrolledRef.current || !sentinel) return;
    if (!isSentinelNearViewport(sentinel) || fetchingRef.current) return;

    fetchingRef.current = true;
    void fetchNextPage().finally(() => {
      fetchingRef.current = false;
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, resetKey]);

  return sentinelRef;
}

export function NotificationPage() {
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const query = useNotificationsInfinite(tab);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const markRead = useMarkReadMutation();
  const markAll = useMarkAllReadMutation();

  const notifications: NotificationItem[] = useMemo(
    () => query.data?.pages?.flatMap((p) => p.data) || [],
    [query.data?.pages],
  );

  const sentinelRef = useNotificationInfiniteScroll({
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
    resetKey: tab,
  });

  const unreadCount = useMemo(() => {
    const fromApi = query.data?.pages?.[0]?.unreadCount;
    if (fromApi !== undefined) return fromApi;
    return notifications.filter((n) => !n.isRead).length;
  }, [query.data?.pages, notifications]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <header className="text-left">
       
        <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
          Notifications
        </h1>
        <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
          Track campaign approvals, wallet updates and account alerts.
        </p>
      </header>

      {/* Tabs + Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 rounded-xl border border-[var(--dash-line)] bg-white p-1">
          <button
            type="button"
            onClick={() => setTab('all')}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              tab === 'all'
                ? 'bg-[var(--dash-brand)] text-white shadow-sm'
                : 'text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setTab('unread')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition ${
              tab === 'unread'
                ? 'bg-[var(--dash-brand)] text-white shadow-sm'
                : 'text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]'
            }`}
          >
            Unread
            {unreadCount > 0 ? (
              <span
                className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                  tab === 'unread'
                    ? 'bg-white text-[var(--dash-brand)]'
                    : 'bg-red-500 text-white'
                }`}
              >
                {unreadCount}
              </span>
            ) : null}
          </button>
        </div>

        <button
          type="button"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending || unreadCount === 0}
          className="inline-flex items-center gap-2 self-start rounded-xl border border-[var(--dash-line)] bg-white px-4 py-2 text-sm font-semibold text-[var(--dash-brand)] disabled:opacity-50 sm:self-auto"
        >
          {markAll.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCheck className="h-4 w-4" />
          )}
          Mark all as read
        </button>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-white shadow-[0_4px_20px_-10px_rgba(16,38,73,0.08)]">
        {query.isPending ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-20 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--dash-soft)]">
              <Bell className="h-8 w-8 text-[var(--dash-muted)] opacity-40" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-[var(--dash-ink)]">
              All caught up!
            </h3>
            <p className="mt-1 max-w-xs text-sm text-[var(--dash-muted)]">
              {tab === 'unread'
                ? "You have no new notifications. We'll let you know when something happens."
                : 'Your notification history is empty.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--dash-line)]/50">
            {notifications.map((n) => {
              const style = getNotifStyle(n.type, n.key);
              return (
                <div
                  key={n._id}
                  className={`group flex items-start gap-4 px-5 py-4 transition ${
                    !n.isRead ? 'bg-[var(--dash-brand-soft)]/30' : 'hover:bg-[var(--dash-soft)]/40'
                  }`}
                >
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${style.bg} ${style.text}`}
                  >
                    {style.icon}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p
                          className={`m-0 text-left text-sm leading-tight ${
                            !n.isRead
                              ? 'font-semibold text-[var(--dash-ink)]'
                              : 'font-medium text-[var(--dash-ink)]/80'
                          }`}
                        >
                          {n.title}
                          {!n.isRead ? (
                            <span className="ml-2 inline-block h-2 w-2 rounded-full bg-[var(--dash-brand)]" />
                          ) : null}
                        </p>
                        {n.body ? (
                          <p className="m-0 mt-1 line-clamp-2 text-left text-xs leading-relaxed text-[var(--dash-muted)]">
                            {n.body}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className="text-[10px] font-semibold text-[var(--dash-muted)]">
                          {getRelativeTime(n.createdAt)}
                        </span>
                        {!n.isRead ? (
                          <button
                            type="button"
                            onClick={() => markRead.mutate(n._id)}
                            className="rounded-lg border border-[var(--dash-line)] bg-white p-1.5 text-[var(--dash-muted)] opacity-0 transition hover:border-[var(--dash-brand)] hover:text-[var(--dash-brand)] group-hover:opacity-100"
                            title="Mark as read"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={sentinelRef} className="h-8" />
          </div>
        )}

        {query.isFetchingNextPage ? (
          <div className="flex items-center justify-center gap-2 border-t border-[var(--dash-line)] py-4">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--dash-brand)]" />
            <span className="text-xs text-[var(--dash-muted)]">
              Loading more…
            </span>
          </div>
        ) : hasNextPage && notifications.length > 0 ? (
          <div className="flex justify-center border-t border-[var(--dash-line)] py-3">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--dash-brand)] hover:bg-[var(--dash-soft)]"
            >
              Load more
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
